import PropTypes from "prop-types"
import React, { useEffect, useState, lazy, Suspense } from "react"

// Import scss
import "./assets/scss/theme.scss"
import "./custom.css"
import "./global.css"
import LoaderSvg from "assets/images/loader-icon.gif"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { Routes, Route, useNavigate } from "react-router-dom"
import { layoutTypes } from "./constants/layout"
// Import Routes all
import { authProtectedRoutes, publicRoutes } from "./routes"
import SweatAlert from "components/Shared/SweatAlert"
import { showAlert } from "store/reducers/alertReducer"

// Import all middleware
import Authmiddleware from "./routes/route"

// layouts Format
const VerticalLayout = lazy(() => import("./components/VerticalLayout/"))
const HorizontalLayout = lazy(() => import("./components/HorizontalLayout/"))
const NonAuthLayout = lazy(() => import("./components/NonAuthLayout"))

// const SweatAlert = lazy(() => import("components/Shared/SweatAlert"))
const Spinners = lazy(() => import("components/Shared/Spinners"))

// Matches SESSION_REPLACED_TOAST_MS in helpers/api_helper.js — the toast
// must stay up for (at least) as long as the interceptor waits before
// redirecting to /login, or the page navigates away mid-toast.
const SESSION_REPLACED_TOAST_MS = 6000

const getLayout = layoutType => {
  let Layout = VerticalLayout
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout
      break
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout
      break
    default:
      break
  }
  return Layout
}

const App = ({ router }) => {
  const selectLayoutState = state => state.Layout
  const LayoutProperties = createSelector(selectLayoutState, layout => ({
    layoutType: layout.layoutType,
  }))
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  const { layoutType } = useSelector(LayoutProperties)

  const Layout = getLayout(layoutType)
  const { isVisible, text, type, nonce, duration } = useSelector(state => state.Alert)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    // Raised by the axios response interceptor (helpers/api_helper.js) when
    // a request comes back 401 with errorCode SESSION_REPLACED — a plain
    // window event instead of an imported action, since that file can't
    // import the redux store without creating a circular dependency.
    const handleSessionReplaced = e => {
      dispatch(
        showAlert({
          text: e.detail?.msg,
          type: "error",
          duration: SESSION_REPLACED_TOAST_MS,
        })
      )
      // Client-side navigation (not window.location.href) so the app
      // doesn't hard-reload once the toast finishes — a full page reload
      // re-mounts everything, which reads as the whole app "loading again"
      // right after the toast disappears.
      const redirectTimeout = setTimeout(() => {
        navigate("/login", { replace: true })
      }, SESSION_REPLACED_TOAST_MS)
      return () => clearTimeout(redirectTimeout)
    }
    window.addEventListener("auth:session-replaced", handleSessionReplaced)
    return () => {
      window.removeEventListener("auth:session-replaced", handleSessionReplaced)
    }
  }, [dispatch, navigate])

  useEffect(() => {
    const splashScreenInterval = setInterval(() => {
      const navState = router.state.navigation.state
      if (navState == "idle") {
        // Hide the splash screen.
        setShowSplashScreen(false)
        clearInterval(splashScreenInterval)
      }
    }, 3000)

    // cleanup in case of component unmount
    ;() => clearInterval(splashScreenInterval)
  }, [])

  return (
    <React.Fragment>
      <>
        {/* {showSplashScreen ? (
          <Suspense fallback={<Spinners />}>
            <Spinners />
          </Suspense>
        ) : ( */}
          <Suspense fallback={<Spinners />}>
            {isVisible && (
              <SweatAlert text={text} type={type} nonce={nonce} duration={duration} />
            )}
            <Routes>
              {publicRoutes.map((route, idx) => (
                <Route
                  path={route.path}
                  element={<NonAuthLayout>{route.component}</NonAuthLayout>}
                  key={idx}
                  exact={true}
                />
              ))}

              {authProtectedRoutes.map((route, idx) => (
                <Route
                  path={route.path}
                  element={
                    <Authmiddleware>
                      <Layout>{route.component}</Layout>
                    </Authmiddleware>
                  }
                  key={idx}
                  exact={true}
                />
              ))}
            </Routes>{" "}
          </Suspense>
        {/* )} */}
      </>
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

export default App
